import { useMutation, useQueryClient } from '@tanstack/react-query';

import { isCommentRepliesDisabledRefusal } from './commentRepliesDisabledRefusal';
import commentReplyService from './commentReplyService';

import type { CreateCommentReplyParams } from './commentReplyService';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import {
    CommentReplyMutationKey,
    CommentReplyQueryKey,
    ReviewInstanceQueryKey,
} from '@/constants/QueryKeys';

export const useCreateCommentReply = (reviewInstanceId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [CommentReplyMutationKey.CREATE_COMMENT_REPLY],
        mutationFn: (params: Omit<CreateCommentReplyParams, 'reviewInstanceId'>) =>
            commentReplyService.createCommentReply({ ...params, reviewInstanceId }),
        // Replies were turned off since the results were read: reading them again closes their reply fields.
        onError: (error) => {
            if (isCommentRepliesDisabledRefusal(error)) {
                void queryClient.invalidateQueries({
                    queryKey: [ReviewInstanceQueryKey.REVIEW_INSTANCE, reviewInstanceId],
                });
            }
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [CommentReplyQueryKey.COMMENT_REPLIES, variables.reviewQuestionAnswerId],
            });

            queryClient.setQueryData<ReviewInstance>(
                [ReviewInstanceQueryKey.REVIEW_INSTANCE, reviewInstanceId],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        reviewAnswers: old.reviewAnswers?.map((reviewAnswer) => ({
                            ...reviewAnswer,
                            reviewQuestionAnswers: reviewAnswer.reviewQuestionAnswers.map(
                                (answer) => {
                                    if (answer.id === variables.reviewQuestionAnswerId) {
                                        return {
                                            ...answer,
                                            commentReplies: [
                                                ...(answer.commentReplies || []),
                                                data,
                                            ],
                                        };
                                    }
                                    return answer;
                                },
                            ),
                        })),
                    };
                },
            );
        },
    });
};
